import type { PopperPlacementType } from '@mui/material';
import {
  ClickAwayListener,
  DialogContent,
  Fade,
  Paper,
  Popper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SwipableDialog } from './swipable-dialog';

/** The parts of a `material-ui-popup-state` popup state the selector reads. */
export interface TixSelectorPopupState {
  popupId?: string;
  isOpen: boolean;
  anchorEl?: Element | null;
  close(): void;
}

// Swipable bottom sheet on mobile, popper anchored to the trigger on desktop.
export function TixSelector({
  children,
  triggerElement,
  open,
  onClose,
  localeState,
  hideCloseButton,
  popperPlacement,
  popperMaxWidth,
}: {
  children: React.ReactNode;
  triggerElement: React.ReactNode;
  open: boolean;
  onClose: () => void;
  localeState: TixSelectorPopupState;
  hideCloseButton?: boolean;
  popperPlacement?: PopperPlacementType;
  popperMaxWidth?: number;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <>
        {triggerElement}
        <SwipableDialog
          open={open}
          onClose={onClose}
          swipable
          hideCloseButton={hideCloseButton}
        >
          <DialogContent sx={{ p: '0.5rem 0.25rem 0.25rem' }}>
            {children}
          </DialogContent>
        </SwipableDialog>
      </>
    );
  }

  return (
    <ClickAwayListener onClickAway={localeState.close}>
      <div>
        {triggerElement}
        <Popper
          id={localeState.popupId}
          anchorEl={localeState.anchorEl}
          open={localeState.isOpen}
          transition
          placement={popperPlacement ?? 'auto'}
          sx={{
            zIndex: 1200,
            width: popperMaxWidth ?? 400,
          }}
        >
          {({ TransitionProps: transitionProps }) => (
            <Fade {...transitionProps} timeout={350}>
              <Paper elevation={8} sx={{ mt: 1, padding: 3 }}>
                <div>{children}</div>
              </Paper>
            </Fade>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
